import React, { useRef, useState, useEffect } from 'react';
import { Button, Space, Typography } from 'antd';
import { ClearOutlined, SaveOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

const { Text } = Typography;

const SignatureCapture = ({ onSave, onCancel, disabled, 'data-testid': testId }) => {
  const { t } = useTranslation();
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [context, setContext] = useState(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    setContext(ctx);
  }, []);

  const startDrawing = (e) => {
    const { offsetX, offsetY } = getCoordinates(e);
    context.beginPath();
    context.moveTo(offsetX, offsetY);
    setIsDrawing(true);
    setHasSignature(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const { offsetX, offsetY } = getCoordinates(e);
    context.lineTo(offsetX, offsetY);
    context.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const getCoordinates = (e) => {
    if (e.touches && e.touches[0]) {
      const rect = canvasRef.current.getBoundingClientRect();
      return {
        offsetX: e.touches[0].clientX - rect.left,
        offsetY: e.touches[0].clientY - rect.top
      };
    }
    return {
      offsetX: e.nativeEvent.offsetX,
      offsetY: e.nativeEvent.offsetY
    };
  };

  const clearSignature = () => {
    context.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    setHasSignature(false);
  };

  const handleSave = () => {
    const signatureData = canvasRef.current.toDataURL('image/png');
    onSave(signatureData);
  };

  return (
    <div style={{ textAlign: 'center', padding: '20px' }}>
      <Text style={{ display: 'block', marginBottom: '16px' }}>
        {t('patient_documents.signature.instructions')}
      </Text>
      
      <canvas
        ref={canvasRef}
        width={400}
        height={200}
        data-testid={testId}
        style={{
          border: '1px solid #d9d9d9',
          borderRadius: '4px',
          touchAction: 'none',
          opacity: disabled ? 0.5 : 1,
          pointerEvents: disabled ? 'none' : 'auto'
        }}
        onMouseDown={startDrawing}
        onMouseMove={draw}
        onMouseUp={stopDrawing}
        onMouseLeave={stopDrawing}
        onTouchStart={startDrawing}
        onTouchMove={draw}
        onTouchEnd={stopDrawing}
      />

      <Space style={{ marginTop: '16px' }}>
        <Button
          icon={<ClearOutlined />}
          onClick={clearSignature}
          disabled={!hasSignature || disabled}
        >
          {t('patient_documents.signature.clear')}
        </Button>
        <Button
          type="primary"
          icon={<SaveOutlined />}
          onClick={handleSave}
          disabled={!hasSignature || disabled}
        >
          {t('patient_documents.signature.save')}
        </Button>
        <Button onClick={onCancel} disabled={disabled}>
          {t('common.cancel')}
        </Button>
      </Space>
    </div>
  );
};

export default SignatureCapture; 